<template>
  <Card>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-medium text-text">Key Expiration Management</h3>
      <Button variant="outline" size="sm" @click="refreshExpirationData" :loading="isLoading">
        <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        Refresh
      </Button>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center h-32">
      <LoadingSpinner />
    </div>

    <div v-else-if="error" class="text-center py-8">
      <Alert variant="error" :message="error" />
    </div>

    <div v-else>
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card class="p-4">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div
                class="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-yellow-600 dark:text-yellow-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Expiring Soon</p>
              <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                {{ expiringKeys.length }}
              </p>
            </div>
          </div>
        </Card>

        <Card class="p-4">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div
                class="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-red-600 dark:text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Expired</p>
              <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                {{ expiredKeys.length }}
              </p>
            </div>
          </div>
        </Card>

        <Card class="p-4">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div
                class="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center"
              >
                <svg
                  class="w-5 h-5 text-green-600 dark:text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Never Expire</p>
              <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                {{ neverExpireCount }}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <!-- Expiring Soon Section -->
      <div v-if="expiringKeys.length > 0" class="mb-6">
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-gray-700 dark:text-gray-200">Keys Expiring Soon</h4>
          <Button
            variant="outline"
            size="sm"
            @click="bulkRegenerateExpiring"
            :disabled="selectedExpiringKeys.length === 0"
          >
            Regenerate Selected ({{ selectedExpiringKeys.length }})
          </Button>
        </div>

        <div
          class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
        >
          <div class="p-4">
            <div class="space-y-3">
              <Card
                v-for="key in expiringKeys"
                :key="key.id"
                padding="sm"
                class="flex items-center justify-between"
              >
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    :value="key.id"
                    v-model="selectedExpiringKeys"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                  />
                  <div>
                    <div class="flex items-center gap-2">
                      <div class="font-medium text-gray-900 dark:text-white">{{ key.name }}</div>
                      <div
                        class="text-xs font-mono text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/50 px-1.5 py-0.5 rounded"
                      >
                        {{ key.key_prefix }}
                      </div>
                    </div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">
                      Expires in {{ key.days_until_expiry }} day{{
                        key.days_until_expiry !== 1 ? 's' : ''
                      }}
                      <span class="mx-1">•</span>
                      {{ formatDate(key.expires_at) }}
                    </div>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <Button variant="outline" size="sm" @click="regenerateExpiringKey(key.id)">
                    Regenerate
                  </Button>
                  <Button variant="outline" size="sm" @click="viewKeyDetails(key.id)">
                    View Details
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <!-- Expired Keys Section -->
      <div v-if="expiredKeys.length > 0" class="mb-6">
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-gray-700 dark:text-gray-200">Expired Keys</h4>
          <Button
            variant="outline"
            size="sm"
            @click="bulkDeleteExpired"
            :disabled="selectedExpiredKeys.length === 0"
            class="text-red-600 hover:text-red-700"
          >
            Delete Selected ({{ selectedExpiredKeys.length }})
          </Button>
        </div>

        <div
          class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
        >
          <div class="p-4">
            <div class="space-y-3">
              <Card
                v-for="key in expiredKeys"
                :key="key.id"
                padding="sm"
                class="flex items-center justify-between opacity-75"
              >
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    :value="key.id"
                    v-model="selectedExpiredKeys"
                    class="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded mr-3"
                  />
                  <div>
                    <div class="flex items-center gap-2">
                      <div class="font-medium text-gray-900 dark:text-white">{{ key.name }}</div>
                      <div
                        class="text-xs font-mono text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/50 px-1.5 py-0.5 rounded"
                      >
                        {{ key.key_prefix }}
                      </div>
                    </div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">
                      Expired {{ key.days_since_expiry }} day{{
                        key.days_since_expiry !== 1 ? 's' : ''
                      }}
                      ago
                      <span class="mx-1">•</span>
                      {{ formatDate(key.expired_at) }}
                    </div>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <Button variant="outline" size="sm" @click="regenerateExpiredKey(key.id)">
                    Regenerate
                  </Button>
                  <Button variant="outline" size="sm" @click="viewKeyDetails(key.id)">
                    View Details
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    @click="deleteExpiredKey(key.id)"
                    class="text-red-600 hover:text-red-700"
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <!-- No Expiration Issues -->
      <div v-if="expiringKeys.length === 0 && expiredKeys.length === 0" class="text-center py-8">
        <div class="text-green-600 dark:text-green-400 mb-2">
          <svg class="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-1">All Set!</h3>
        <p class="text-gray-500 dark:text-gray-400">
          No API keys require immediate attention for expiration.
        </p>
      </div>
    </div>

    <!-- RegenerateApiKeyModal -->
    <RegenerateApiKeyModal
      v-model:open="showRegenerateModal"
      :api-key="selectedKeyForRegeneration"
      @regenerated="handleRegenerateCompleted"
    />

    <!-- View Key Details Modal -->
    <Dialog
      :open="showViewModal"
      @update:open="showViewModal = $event"
      title="API Key Details"
      size="lg"
    >
      <div v-if="selectedKeyDetails" class="space-y-6">
        <!-- Key Information -->
        <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">Key Information</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                >Name</label
              >
              <p class="text-sm text-gray-900 dark:text-white mt-1">
                {{ selectedKeyDetails.name }}
              </p>
            </div>
            <div>
              <label
                class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                >Key ID</label
              >
              <p class="text-sm text-gray-900 dark:text-white mt-1 font-mono">
                {{ selectedKeyDetails.id }}
              </p>
            </div>
            <div>
              <label
                class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                >Masked Key</label
              >
              <p class="text-sm text-gray-900 dark:text-white mt-1 font-mono">
                {{ getMaskedKey(selectedKeyDetails) }}
              </p>
            </div>
            <div>
              <label
                class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                >Status</label
              >
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1"
                :class="
                  isKeyActive
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                "
              >
                {{ isKeyActive ? 'Active' : 'Inactive' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Timestamps -->
        <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">Timestamps</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                >Created</label
              >
              <p class="text-sm text-gray-900 dark:text-white mt-1">
                {{ formatDate(selectedKeyDetails.created_at) }}
              </p>
            </div>
            <div>
              <label
                class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                >Last Updated</label
              >
              <p class="text-sm text-gray-900 dark:text-white mt-1">
                {{ formatDate(selectedKeyDetails.created_at) }}
              </p>
            </div>
            <div>
              <label
                class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                >                Last Used</label
              >
              <p class="text-sm text-gray-900 dark:text-white mt-1">N/A</p>
            </div>
            <div>
              <label
                class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide"
                >Expires</label
              >
              <p class="text-sm text-gray-900 dark:text-white mt-1">
                {{
                  selectedKeyDetails.expires_at
                    ? formatDate(selectedKeyDetails.expires_at)
                    : 'Never'
                }}
              </p>
            </div>
          </div>
        </div>

        <!-- Permissions -->
        <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">Permissions</h4>
          <div class="text-sm text-gray-600 dark:text-gray-300">
            <pre
              class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-3 text-xs overflow-auto text-gray-900 dark:text-gray-100"
              >{{ JSON.stringify(selectedKeyDetails.permissions, null, 2) }}</pre
            >
          </div>
        </div>
      </div>

      <template #actions>
        <Button variant="outline" @click="showViewModal = false"> Close </Button>
      </template>
    </Dialog>
  </Card>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue';
  import { Button, LoadingSpinner, Alert, Card, Dialog, useNotification } from '@asabdev/asab-ui';
  import RegenerateApiKeyModal from './RegenerateApiKeyModal.vue';
  import type { ApiKey } from '../types';
  import { useApiKeys } from '../composables';
  import { formatApiKeyDate, maskApiKey, isApiKeyExpired, isApiKeyExpiringSoon } from '../utils';

  interface ExpiringKey {
    id: string;
    name: string;
    key_prefix: string;
    expires_at: string;
    days_until_expiry: number;
  }

  interface ExpiredKey {
    id: string;
    name: string;
    key_prefix: string;
    expired_at: string;
    days_since_expiry: number;
  }

  const { showSuccess, showError } = useNotification();
  const {
    apiKeys,
    isLoading,
    error,
    refresh,

    deleteApiKey,
  } = useApiKeys();

  const selectedExpiringKeys = ref<string[]>([]);
  const selectedExpiredKeys = ref<string[]>([]);
  const showRegenerateModal = ref(false);
  const selectedKeyForRegeneration = ref<ApiKey | null>(null);

  // View modal state
  const showViewModal = ref(false);
  const selectedKeyDetails = ref<ApiKey | null>(null);

  const isKeyActive = computed(() => {
    const key = selectedKeyDetails.value;
    if (!key) return false;
    if (key.revoked_at) return false;
    if (key.expires_at && new Date(key.expires_at) <= new Date()) return false;
    return true;
  });

  // Computed properties for expiring and expired keys
  const expiringKeys = computed((): ExpiringKey[] => {
    return apiKeys.value
      .filter((key: ApiKey) => isApiKeyExpiringSoon(key))
      .map((key: ApiKey) => {
        const expiryDate = new Date(key.expires_at!);
        const today = new Date();
        const diffTime = expiryDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return {
          id: key.id,
          name: key.name,
          key_prefix: getMaskedKey(key),
          expires_at: key.expires_at!,
          days_until_expiry: diffDays,
        };
      });
  });

  const expiredKeys = computed((): ExpiredKey[] => {
    return apiKeys.value
      .filter((key: ApiKey) => isApiKeyExpired(key))
      .map((key: ApiKey) => {
        const expiredDate = new Date(key.expires_at!);
        const today = new Date();
        const diffTime = today.getTime() - expiredDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return {
          id: key.id,
          name: key.name,
          key_prefix: getMaskedKey(key),
          expired_at: key.expires_at!,
          days_since_expiry: diffDays,
        };
      });
  });

  const neverExpireCount = computed(() => {
    return apiKeys.value.filter((key: ApiKey) => !key.expires_at).length;
  });

  const formatDate = (dateString: string): string => {
    return formatApiKeyDate(dateString);
  };

  const getMaskedKey = (key: ApiKey): string => {
    return maskApiKey(key.key_prefix);
  };

  const refreshExpirationData = async () => {
    try {
      await refresh();
    } catch {
      showError('Failed to fetch expiration data');
    }
  };

  const regenerateExpiringKey = (keyId: string) => {
    // Find the API key object to pass to the modal
    const key = apiKeys.value.find((k: ApiKey) => k.id === keyId);
    if (key) {
      selectedKeyForRegeneration.value = key;
      showRegenerateModal.value = true;
    } else {
      showError('Key not found');
    }
  };

  const bulkRegenerateExpiring = () => {
    if (selectedExpiringKeys.value.length === 0) return;

    // For now, we'll handle one key at a time using the existing modal
    // In the future, this could be enhanced to handle multiple keys
    const firstKeyId = selectedExpiringKeys.value[0];
    const key = apiKeys.value.find((k: ApiKey) => k.id === firstKeyId);

    if (key) {
      selectedKeyForRegeneration.value = key;
      showRegenerateModal.value = true;
    } else {
      showError('Key not found');
    }
  };

  const regenerateExpiredKey = (keyId: string) => {
    // Find the API key object to pass to the modal
    const key = apiKeys.value.find((k: ApiKey) => k.id === keyId);
    if (key) {
      selectedKeyForRegeneration.value = key;
      showRegenerateModal.value = true;
    } else {
      showError('Key not found');
    }
  };

  const handleRegenerateCompleted = async () => {
    await refreshExpirationData();
    selectedExpiringKeys.value = [];
    showSuccess('API key regenerated successfully');
  };

  const bulkDeleteExpired = async () => {
    if (selectedExpiredKeys.value.length === 0) return;

    if (
      confirm(
        `Are you sure you want to delete ${selectedExpiredKeys.value.length} expired key${selectedExpiredKeys.value.length > 1 ? 's' : ''}?`
      )
    ) {
      try {
        for (const keyId of selectedExpiredKeys.value) {
          await deleteApiKey(keyId);
        }
        selectedExpiredKeys.value = [];
        await refreshExpirationData();
      } catch {
        showError('Failed to delete expired keys');
      }
    }
  };

  const deleteExpiredKey = async (keyId: string) => {
    if (confirm('Are you sure you want to delete this expired key?')) {
      try {
        await deleteApiKey(keyId);
        showSuccess('Expired key deleted');
        await refreshExpirationData();
      } catch {
        showError('Failed to delete expired key');
      }
    }
  };

  const viewKeyDetails = (keyId: string) => {
    // Find the key in the API keys list
    const key = apiKeys.value.find((k: ApiKey) => k.id === keyId);
    if (key) {
      selectedKeyDetails.value = key;
      showViewModal.value = true;
    } else {
      showError('Key not found');
    }
  };

  onMounted(() => {
    refreshExpirationData();
  });
</script>
